import React from "react";
import {
  BookOutlined,
  CloseOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import JoinLecture from "Pages/MyScheduleBeta/LectureDetails/CommonComponents/JoinLecture";
import useResponsive from "Context/useResponsive";
import NotMarkedNew from "Assets/Icons/NotMarkedIconNew";
import { inBetweenTime } from "utilFunctions/shared/ScheduleStatus";
import { isShowJoinLectureButton } from "Pages/MyScheduleBeta/utils/layoutHelpers";

export interface LectureDetailsPopoverContentProps {
  event: any;
  onClose: () => void;
  onOpenDetails: (event: any) => void;
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
};

const LectureDetailsPopoverContent: React.FC<
  LectureDetailsPopoverContentProps
> = ({ event, onClose, onOpenDetails }) => {
  const { isMobile } = useResponsive();
  const isOngoing = inBetweenTime(event?.startTime, event?.endTime);
  const showUserIcon = event?.attendanceStatus === "Unmarked";
  const showAccountBookIcon =
    event?.syllabusStatus === "Unmarked" && !isOngoing;
  const message = ["PTM", "ORIENTATION"].includes(event.eventType)
    ? "Join Meeting"
    : "Join Lecture";
  return (
    <div
      style={{
        width: isMobile ? "100%" : "400px",
        maxWidth: "100%",
        padding: isMobile ? "16px" : "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              backgroundColor: "#e1f5fe",
              color: "#0288d1",
              padding: "4px 12px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {event?.eventType || "LECTURE"}
          </div>

          {showUserIcon && (
            <div
              style={{
                width: "30px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <UserOutlined />
              <NotMarkedNew
                style={{
                  position: "absolute",
                  width: "14px",
                  top: "13px",
                  left: "21px",
                }}
              />
            </div>
          )}

          {showAccountBookIcon && (
            <div
              style={{
                width: "30px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <BookOutlined />
              <NotMarkedNew
                style={{
                  position: "absolute",
                  width: "14px",
                  top: "13px",
                  left: "21px",
                }}
              />
            </div>
          )}
        </div>
        <CloseOutlined
          style={{ cursor: "pointer", color: "#757575", fontSize: "16px" }}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
      </div>

      <div
        style={{
          fontSize: isMobile ? "12px" : "18px",
          fontWeight: 700,
          color: "#212121",
          marginBottom: "8px",
        }}
      >
        {event.batchName || event.eventName || "N/A"}
      </div>
      <div style={{ fontSize: "14px", color: "#5f6368", marginBottom: "24px" }}>
        {formatDate(event.startTime)} · {formatTime(event.startTime)} -{" "}
        {formatTime(event.endTime)}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#9e9e9e",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            LECTURE ID
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#212121",
              fontWeight: 500,
              marginTop: "6px",
            }}
          >
            {event.batchTimeTableId || "N/A"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#9e9e9e",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            CLASS
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#212121",
              fontWeight: 500,
              marginTop: "6px",
            }}
          >
            {event.class || "N/A"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#9e9e9e",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            SUBJECT
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#212121",
              fontWeight: 500,
              marginTop: "6px",
            }}
          >
            {event.subject || "N/A"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#9e9e9e",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            ROOM ID
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "#212121",
              fontWeight: 500,
              marginTop: "6px",
            }}
          >
            {event.room || "N/A"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        {isShowJoinLectureButton(event) && (
          <JoinLecture eventLink={event.eventLink} message={message} />
        )}
        <Button
          style={{
            flex: 1,
            borderRadius: "8px",
            borderColor: "#bdbdbd",
            color: "#424242",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(event);
          }}
          size={isMobile ? "small" : "large"}
        >
          Open full details{" "}
          <RightOutlined style={{ fontSize: "12px", marginLeft: "4px" }} />
        </Button>
      </div>
    </div>
  );
};

export default LectureDetailsPopoverContent;
